<script setup lang="ts">
/**
 * Module: web/src/views/ChangePasswordView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Change password -- forced-change aware (doc 04 sect 6.3). While
 * must_change_password is TRUE the router guard confines the session here and
 * the shell hides all nav; this view shows the forced banner. Also reachable
 * voluntarily from the user menu. Policy errors (400 {code:'policy', detail})
 * render verbatim; 401 = wrong current password.
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { ApiError } from '@/composables/useApi';
import AppIcon from '@/components/AppIcon.vue';

const auth = useAuthStore();
const router = useRouter();

const currentPw = ref('');
const newPw = ref('');
const confirmPw = ref('');
const showNew = ref(false);
const submitting = ref(false);
const errorMessage = ref('');
const done = ref(false);

const MIN_LENGTH = 12; // policy sect 8.4

const newPwTooShort = computed(() => newPw.value.length > 0 && newPw.value.length < MIN_LENGTH);
const confirmMismatch = computed(() => confirmPw.value.length > 0 && confirmPw.value !== newPw.value);

async function submit() {
  if (submitting.value) return;
  errorMessage.value = '';
  if (!currentPw.value || !newPw.value) {
    errorMessage.value = 'Fill in every field.';
    return;
  }
  if (newPw.value.length < MIN_LENGTH) {
    errorMessage.value = `New password must be at least ${MIN_LENGTH} characters.`;
    return;
  }
  if (newPw.value !== confirmPw.value) {
    errorMessage.value = 'New password and confirmation do not match.';
    return;
  }
  submitting.value = true;
  try {
    await auth.changePassword(currentPw.value, newPw.value);
    done.value = true;
    // Brief success beat, then land on the role Workspace (sect 9.5).
    setTimeout(() => void router.push(auth.landingPath), 900);
  } catch (e) {
    if (e instanceof ApiError && e.status === 400 && e.code === 'policy') {
      errorMessage.value = e.detail ?? 'New password does not meet the password policy.';
    } else if (e instanceof ApiError && e.status === 401) {
      errorMessage.value = 'Current password is incorrect.';
    } else {
      errorMessage.value = 'Password change failed. Try again.';
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col items-center px-4 py-8 md:py-14">
    <div class="w-full max-w-sm">
      <div v-if="auth.mustChange" class="alert-info mb-6 flex items-start gap-2.5">
        <AppIcon name="alert" class="w-5 h-5 flex-shrink-0 mt-0.5" />
        <span>You must set a new password before using the portal.</span>
      </div>

      <h1 class="text-xl font-bold text-fg mb-1">Change password</h1>
      <p class="text-sm text-fg-muted mb-6">
        At least {{ MIN_LENGTH }} characters. Not your email, your name, or the temporary password.
      </p>

      <div v-if="done" class="alert-success flex items-center gap-2.5" role="status">
        <AppIcon name="check" class="w-5 h-5 flex-shrink-0" />
        Password changed. Taking you to your workspace…
      </div>

      <form v-else class="space-y-4" novalidate @submit.prevent="submit">
        <div v-if="errorMessage" class="alert-error" role="alert">{{ errorMessage }}</div>

        <div>
          <label class="field-label" for="cp-current">Current password</label>
          <input
            id="cp-current"
            v-model="currentPw"
            class="field-input"
            type="password"
            autocomplete="current-password"
            :disabled="submitting"
          />
        </div>

        <div>
          <label class="field-label" for="cp-new">New password</label>
          <div class="relative">
            <input
              id="cp-new"
              v-model="newPw"
              class="field-input pr-12"
              :type="showNew ? 'text' : 'password'"
              autocomplete="new-password"
              :disabled="submitting"
            />
            <button
              type="button"
              class="absolute inset-y-0 right-0 px-3 text-fg-muted hover:text-fg"
              :aria-label="showNew ? 'Hide password' : 'Show password'"
              @click="showNew = !showNew"
            >
              <AppIcon :name="showNew ? 'eye-off' : 'eye'" class="w-5 h-5" />
            </button>
          </div>
          <p v-if="newPwTooShort" class="mt-1.5 text-xs text-accent-bad">
            {{ MIN_LENGTH - newPw.length }} more character{{ MIN_LENGTH - newPw.length === 1 ? '' : 's' }} needed.
          </p>
        </div>

        <div>
          <label class="field-label" for="cp-confirm">Confirm new password</label>
          <input
            id="cp-confirm"
            v-model="confirmPw"
            class="field-input"
            :type="showNew ? 'text' : 'password'"
            autocomplete="new-password"
            :disabled="submitting"
          />
          <p v-if="confirmMismatch" class="mt-1.5 text-xs text-accent-bad">Does not match the new password.</p>
        </div>

        <button type="submit" class="btn-primary w-full" :disabled="submitting">
          {{ submitting ? 'Saving…' : 'Change password' }}
        </button>
      </form>
    </div>
  </div>
</template>
